"use client";

import React, { useState } from "react";
import { Section, Typography } from "@/components/ui";

interface Feature {
  id: number;
  title: string;
  content: string[];
  note: string;
}

interface KeyFeatureSectionProps {
  title: string;
  description: string;
  features: Feature[];
  className?: string;
}

export function KeyFeatureSection({
  title,
  description,
  features,
  className = "bg-secondary/5",
}: KeyFeatureSectionProps) {
  const [activeFeature, setActiveFeature] = useState(0);

  return (
    <Section
      className={`${className}`}
    >
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Typography as="h2" variant="h2">
            {title}
          </Typography>
          <Typography as="p" variant="body" className="text-text-secondary leading-relaxed mt-4">
            {description}
          </Typography>
        </div>

        {/* Desktop Layout - Sidebar + Content */}
        <div className="hidden lg:grid grid-cols-3 gap-8">
          {/* Left Sidebar - Feature Navigation */}
          <div className="space-y-4">
            {features.map((feature, index) => (
              <button
                key={feature.id}
                type="button"
                onClick={() => setActiveFeature(index)}
                aria-label={`Feature ${feature.id}: ${feature.title}`}
                className={`group w-full text-left p-4 rounded-lg border border-gray-200 transition-all duration-200 ${
                  index === activeFeature
                    ? "bg-primary text-white"
                    : "bg-white text-text-primary hover:bg-primary hover:text-white"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                  <span className="font-medium">
                    {feature.id}
                  </span>
                  <span className="font-medium">
                    {feature.title}
                  </span>
                  </div>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200 ${
                    index === activeFeature
                      ? "bg-white"
                      : "bg-transparent group-hover:bg-white"
                  }`}>
                    <svg
                      className={`w-3 h-3 transition-all duration-200 ${
                        index === activeFeature
                          ? "text-accent"
                          : "text-transparent group-hover:text-accent"
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Right Content - Feature Details */}
          <div className="col-span-2">
            {features[activeFeature] && (
              <div key={features[activeFeature].id} className="space-y-6">
                {/* Feature Details */}
                <div>
                  <ul className="space-y-3">
                    {features[activeFeature].content.map((item, index) => (
                      <li key={index} className="list-disc list-inside">
                        <span className="text-text-primary">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* What's Not Available */}
                <div className="bg-white border border-gray-200 p-4 rounded-lg">
                  <Typography as="h4" variant="h6" className="mb-2 text-text-primary font-semibold">
                    What features aren&apos;t available?
                  </Typography>
                  <Typography as="p" className="text-text-primary">
                    {features[activeFeature].note}
                  </Typography>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Layout - Individual Feature Cards */}
        <div className="lg:hidden space-y-8">
          {features.map((feature, index) => (
            <div key={feature.id} className="w-full">
              {/* Feature Title with Number Badge */}
              <div className="flex items-start gap-4 mb-6">
                <div className="flex-shrink-0 w-10 h-10 bg-secondary text-white rounded-full flex items-center justify-center font-bold text-lg">
                  {feature.id}
                </div>
                <Typography as="h3" variant="h3" className="text-text-primary font-semibold">
                  {feature.title}
                </Typography>
              </div>

              {/* Feature Details */}
              <div className="mb-6">
                <ul className="space-y-3">
                  {feature.content.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start list-disc list-inside">
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* What's Not Available */}
              <div className="bg-white border border-gray-200 p-4 rounded-lg">
                <Typography as="h4" variant="h6" className="mb-2 text-gray-800 font-semibold">
                  What features aren&apos;t available?
                </Typography>
                <Typography as="p" className="text-text-primary">
                  {feature.note}
                </Typography>
              </div>
            </div>
          ))}
        </div>
    </Section>
  );
}
